import { Eye, Loader2, Play, ListPlus, type LucideIcon } from 'lucide-react';
import { NowPlayingIndicator } from '@/components/audio';
import type { FavoriteItem, FavoriteCachedData } from '@/types/favorites';

interface FavoriteItemCardContentProps {
  item: FavoriteItem;
  cachedData: FavoriteCachedData;
  Icon: LucideIcon;
  typeLabel: string;
  colors: { bg: string; text: string; border: string };
  loading: boolean;
  isAudioFile: boolean;
  isThisTrackPlaying: boolean;
  isThisTrackLoaded: boolean;
  isViewable: boolean;
}

export function FavoriteItemCardContent({
  item,
  cachedData,
  Icon,
  typeLabel,
  colors,
  loading,
  isAudioFile,
  isThisTrackPlaying,
  isThisTrackLoaded,
  isViewable,
}: FavoriteItemCardContentProps) {
  const audioFileIconWhenNotPlaying = isThisTrackLoaded ? (
    <Play size={18} className="text-[#8e4585]" />
  ) : (
    <ListPlus size={18} className={colors.text} />
  );
  const audioFileIcon = isThisTrackPlaying ? (
    <NowPlayingIndicator size="sm" />
  ) : (
    audioFileIconWhenNotPlaying
  );
  const viewActionWhenPlaying = (
    <>
      <NowPlayingIndicator size="sm" />
      <span className="ml-1">Playing</span>
    </>
  );
  const eyeViewContent = (
    <>
      <Eye size={12} />
      View
    </>
  );
  const iconWhenNotLoading = isAudioFile ? (
    audioFileIcon
  ) : (
    <Icon size={18} className={colors.text} />
  );
  const viewIndicatorOpacityClass = isThisTrackPlaying
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100';
  const viewIndicatorContent = (() => {
    if (!isAudioFile) return eyeViewContent;
    if (isThisTrackPlaying) return viewActionWhenPlaying;
    if (isThisTrackLoaded)
      return (
        <>
          <Play size={12} />
          Resume
        </>
      );
    return (
      <>
        <ListPlus size={12} />
        Play
      </>
    );
  })();

  return (
    <div className="flex items-start gap-3">
      {/* Type Icon / Thumbnail / Loading / Audio playing indicator */}
      {item.itemType === 'file' && cachedData.thumbnailUrl && !loading ? (
        <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden relative">
          <img
            src={cachedData.thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {isAudioFile && isThisTrackPlaying && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <NowPlayingIndicator size="sm" className="[&>div]:bg-white" />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`p-2 rounded-lg flex-shrink-0 ${
            isThisTrackPlaying ? 'bg-[#f8e8f8]' : colors.bg
          }`}
        >
          {loading ? (
            <Loader2 size={18} className={`${colors.text} animate-spin`} />
          ) : (
            iconWhenNotLoading
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Type Badge + View indicator */}
        <div className="flex items-center gap-2 mb-1">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
          >
            {typeLabel}
          </span>
          {cachedData.authorName && (
            <span className="text-xs text-slate-400">by {cachedData.authorName}</span>
          )}
          {/* View indicator on hover */}
          {isViewable && (
            <span
              className={`ml-auto flex items-center gap-1 text-xs text-[#8e4585] transition-opacity ${viewIndicatorOpacityClass}`}
            >
              {viewIndicatorContent}
            </span>
          )}
        </div>

        {/* Title */}
        <h4 className="font-medium text-slate-800 truncate mb-1">
          {cachedData.title || 'Untitled'}
        </h4>

        {/* Image thumbnail for image-type files */}
        {item.itemType === 'file' && cachedData.fileType === 'image' && cachedData.url && (
          <div className="mb-2">
            <img
              src={cachedData.url}
              alt={cachedData.title}
              className="w-16 h-16 object-cover rounded-lg border border-slate-200"
            />
          </div>
        )}

        {/* Image thumbnail for messages with images */}
        {item.itemType === 'message' && cachedData.imageUrl && (
          <div className="mb-2">
            <img
              src={cachedData.imageUrl}
              alt="Post attachment"
              className="w-16 h-16 object-cover rounded-lg border border-slate-200"
            />
          </div>
        )}

        {/* Preview text (skip for items that have image thumbnails) */}
        {cachedData.preview &&
          !(item.itemType === 'file' && cachedData.fileType === 'image') &&
          !(item.itemType === 'message' && cachedData.imageUrl) && (
            <p className="text-sm text-slate-500 line-clamp-2 mb-2">{cachedData.preview}</p>
          )}

        {/* Date */}
        <div className="text-xs text-slate-400">
          Saved{' '}
          {item.createdAt?.toDate
            ? item.createdAt.toDate().toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year:
                  item.createdAt.toDate().getFullYear() !== new Date().getFullYear()
                    ? 'numeric'
                    : undefined,
              })
            : ''}
        </div>
      </div>
    </div>
  );
}
